import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import axios from "axios";
import { automaticImportApi } from "libs/api";
import type { ExtendedScrapingErrorResponse } from "libs/codegen/property-onboarding";
import { useNavigate, useParams } from "router";
import { scrapingErrorResponseMapper } from "../urlInput/scrapingErrorResponseMapper.ts";
import { useSetProperties } from "./propertiesAtom.ts";
import { selectablePropertiesFilter } from "./selectablePropertiesFilter.ts";
import { useSetSelectedProperties } from "./selectedPropertiesAtom.ts";

export const usePropertyListing = () => {
    const navigate = useNavigate();
    const { lang } = useParams("/:lang/list-my-property/get-started/automatic/lists");

    const setProperties = useSetProperties();
    const setSelectedProperties = useSetSelectedProperties();

    const handleScrapingErrorResponse = (e: AxiosError<ExtendedScrapingErrorResponse>) => {
        if (axios.isAxiosError(e)) {
            navigate("/:lang/list-my-property/get-started/automatic", {
                params: {
                    lang,
                },
                state: {
                    errorResponse: e.response?.data,
                },
            });
        } else {
            navigate("/:lang/list-my-property/get-started/automatic", {
                params: {
                    lang,
                },
                state: {
                    errorResponse: {
                        errorType: scrapingErrorResponseMapper.UnexpectedError,
                        propertyItem: null,
                    } satisfies ExtendedScrapingErrorResponse,
                },
            });
        }
    };

    const { isIdle, mutate } = useMutation({
        mutationFn: (sanitizedUrl: string) => automaticImportApi.getPropertyListing(lang, sanitizedUrl),
        onSuccess: async ({ data: properties }) => {
            if (properties.length === 1 && selectablePropertiesFilter(properties[0])) {
                try {
                    const { data } = await automaticImportApi.importProperty(lang, {
                        selected: properties.filter(selectablePropertiesFilter),
                        unselected: [],
                    });

                    window.location.assign(data.redirectTo);
                } catch (e) {
                    handleScrapingErrorResponse(e as AxiosError<ExtendedScrapingErrorResponse>);
                }
            } else {
                // force delay at least 2 seconds per design request
                await new Promise((r) => setTimeout(r, 2000));

                setProperties(properties);
                setSelectedProperties(properties.filter(selectablePropertiesFilter).map((p) => p.id));
            }
        },
        onError: handleScrapingErrorResponse,
    });

    return {
        isIdle,
        mutate,
    };
};
